import { Container } from '@mui/material'
import Navbar from '../../components/navbars/Navbar'
import ListTable from '../../components/tables/ListTable'
import Details from '../../components/details/Details'
import Breadcrumbs from '../../components/breadcrumbs/Breadcrumbs'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { accessSscc } from '../../services/axios/functions/list.function'
import { useQuery } from '@tanstack/react-query'
import { getNestedValue } from '../../utils/getNestedValue'
import APIErrorPage from '../APIError/APIErrorPage'
import { checkForOneLevelSSCC } from '../../utils/checkForOneLevelSSCC'
import { errorMessage } from '../../constants/error-message'

const Dashboard = () => {
  const [currentList, setCurrentList] = useState([])
  const [quantity, setQuantity] = useState('N/A')
  const [path, setPath] = useState('')
  const [details, setDetails] = useState(null)
  const navigate = useNavigate()
  // const [searchParams] = useSearchParams()
  const { state } = useLocation()

  // Get URL parameters
  const sscc = state?.sscc
  const po = state?.po

  // Query for SSCC data
  const {
    data: fullData,
    isLoading,
    isError,
  } = useQuery<any>({
    queryKey: ['sscc', sscc, po],
    queryFn: () => accessSscc(sscc as string, po as string),
    enabled: !!sscc && !!po,
    select: (res) => res?.data,
    staleTime: 36000,
  })

  const data = fullData ? fullData[0]?.epcisJsonList : undefined

  useEffect(() => {
    if (!sscc && !po) {
      navigate(`/`)
    }
  }, [sscc, po, navigate])

  // Helper function to traverse data based on path
  const getDataAtPath = (fullData: any, pathStr: string) => {
    const data = fullData && Array.isArray(fullData) ? fullData[0]?.parsedJsonDto : []
    if (!pathStr) {
      if (data && Array.isArray(data) && data.length > 0 && checkForOneLevelSSCC(fullData)) {
        setDetails({
          itemExpirationDate: data[0]?.itemExpirationDate,
          lotNumber: data[0]?.lotNumber,
          name: data[0]?.name,
          sgtin: data[0]?.sgtin,
          itemIdentification: data[0]?.itemIdentification,
        })
      } else {
        setDetails({})
      }

      return {
        list: data && Array.isArray(data) ? data.map((item) => item?.sgtin) : [],
        quantity: data && Array.isArray(data) ? data.length : 'N/A',
      }
    }

    const pathArray = pathStr.split(',')

    const current = getNestedValue(data, pathArray)

    if ('itemExpirationDate' in current) {
      setDetails({
        itemExpirationDate: current?.itemExpirationDate,
        lotNumber: current?.lotNumber,
        name: current?.name,
        sgtin: current?.sgtin,
        itemIdentification: current?.itemIdentification,
      })
    } else {
      setDetails({})
    }

    return {
      list:
        current && 'subChild' in current && Array.isArray(current?.subChild)
          ? current?.subChild?.map((item: { sgtin: 'string' }) => item?.sgtin)
          : [],
      quantity:
        current && 'subChild' in current && Array.isArray(current?.subChild)
          ? current?.subChild?.length
          : 0,
    }
  }

  // Update current list and quantity when data or path changes
  useEffect(() => {
    if (data && Array.isArray(data) && data.length > 0) {
      const result = getDataAtPath(data, path)
      if (result?.list) {
        setCurrentList(result.list)
        setQuantity(result.quantity)
      }
    }
  }, [data, path])

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (index: number) => {
    const pathArray = path ? path.split(',') : []
    const newPath = pathArray.slice(0, index).join(',')
    setPath(newPath)
    sessionStorage.setItem(`path_${sscc}_${po}`, newPath)
  }

  useEffect(() => {
    const storedPath = sessionStorage.getItem(`path_${sscc}_${po}`)
    if (storedPath) {
      setPath(storedPath)
    }
  }, [sscc, po])

  // Handle row click
  const handleRowClick = (currentRow: string) => {
    const newPath = path ? `${path},${currentRow}` : currentRow
    setPath(newPath)
    sessionStorage.setItem(`path_${sscc}_${po}`, newPath)
  }

  if (isLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '4',
        }}
      >
        <span style={{ marginBottom: '25px' }}>Loading...</span>
        <svg width="135" height="60" viewBox="0 0 100 60" fill="#2db47d">
          <rect x="0" y="0" width="10" height="60" rx="6">
            <animate
              attributeType="CSS"
              attributeName="height"
              values="60;20;60;"
              begin="0s"
              dur="1s"
              repeatCount="indefinite"
            />
            <animate
              attributeType="CSS"
              attributeName="y"
              begin="0s"
              values="0;20;0;"
              dur="1s"
              repeatCount="indefinite"
            />
          </rect>
          <rect x="20" y="0" width="10" height="60" rx="6">
            <animate
              attributeType="CSS"
              attributeName="height"
              values="60;20;60"
              begin="0.2s"
              dur="1s"
              repeatCount="indefinite"
            />
            <animate
              attributeType="CSS"
              attributeName="y"
              values="0;20;0"
              begin="0.2s"
              dur="1s"
              repeatCount="indefinite"
            />
          </rect>
          <rect x="40" y="0" width="10" height="60" rx="6">
            <animate
              attributeType="CSS"
              attributeName="height"
              values="60;20;60"
              begin="0.4s"
              dur="1s"
              repeatCount="indefinite"
            />
            <animate
              attributeType="CSS"
              attributeName="y"
              values="0;20;0"
              begin="0.4s"
              dur="1s"
              repeatCount="indefinite"
            />
          </rect>
          <rect x="60" y="0" width="10" height="60" rx="6">
            <animate
              attributeType="CSS"
              attributeName="height"
              values="60;20;60"
              begin="0.6s"
              dur="1s"
              repeatCount="indefinite"
            />
            <animate
              attributeType="CSS"
              attributeName="y"
              values="0;20;0"
              begin="0.6s"
              dur="1s"
              repeatCount="indefinite"
            />
          </rect>
          <rect x="80" y="0" width="10" height="60" rx="6">
            <animate
              attributeType="CSS"
              attributeName="height"
              values="60;20;60"
              begin="0.8s"
              dur="1s"
              repeatCount="indefinite"
            />
            <animate
              attributeType="CSS"
              attributeName="y"
              values="0;20;0"
              begin="0.8s"
              dur="1s"
              repeatCount="indefinite"
            />
          </rect>
        </svg>
      </div>
    )
  }

  if (isError || (fullData && 'status' in fullData)) {
    const statusCode = (fullData?.status as keyof typeof errorMessage) || 500
    return (
      <APIErrorPage
        fileUrl={''}
        msg={errorMessage[statusCode]?.msg}
        title={errorMessage[statusCode]?.title}
        sscc={sscc}
        po={po}
      />
    )
  }

  // Get breadcrumb IDs from path
  const breadcrumbIds = path ? [`SSCC ${sscc}`, ...path.split(',')] : sscc ? [`SSCC ${sscc}`] : []

  if (!sscc || !po) return <></>

  return (
    <>
      <Navbar
        fileUrl={
          fullData && Array.isArray(fullData) && fullData.length > 0 ? fullData[0]?.fileUrl : ''
        }
        sscc={sscc}
        po={po}
      />
      <Container maxWidth="lg">
        {checkForOneLevelSSCC(data) || breadcrumbIds.length > 1 ? (
          <Breadcrumbs
            ids={breadcrumbIds}
            onBreadcrumbClick={handleBreadcrumbClick}
            singleLevelSSCC={checkForOneLevelSSCC(data)}
          />
        ) : null}
        {details && 'itemIdentification' in details && (
          <Details
            details={{
              ndc: details?.itemIdentification,
              description: details?.name as string,
              exp: details?.itemExpirationDate as string,
              lot: details?.lotNumber as string,
              quantity: (quantity || 'N/A') as string,
            }}
          />
        )}
        {/* {currentList && ( */}
        <ListTable
          fullData={data}
          data={currentList}
          handleClickRow={handleRowClick}
          ids={breadcrumbIds}
          path={path}
          singleLevelSSCC={checkForOneLevelSSCC(data)}
        />
        {/* )} */}
      </Container>
    </>
  )
}

export default Dashboard
